import React from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import SearchableSelect from '@/components/shared/SearchableSelect';
import PersianDatePickerFa from '@/components/shared/PersianDatePickerFa';
import type { AccountingDocumentsFilterState } from '@/lib/accountingDocumentFilters';

type AccountingDocumentsToolbarProps = {
  filters: AccountingDocumentsFilterState;
  onChange: (patch: Partial<AccountingDocumentsFilterState>) => void;
  onClear: () => void;
  resultCount: number;
  totalCount: number;
  showProjectFilter?: boolean;
};

const BALANCE_OPTIONS = [
  { value: 'all', label: 'همهٔ اسناد' },
  { value: 'balanced', label: 'فقط تراز' },
  { value: 'unbalanced', label: 'دارای اختلاف' },
];

const AccountingDocumentsToolbar: React.FC<AccountingDocumentsToolbarProps> = ({
  filters,
  onChange,
  onClear,
  resultCount,
  totalCount,
  showProjectFilter = true,
}) => {
  const hasActive =
    filters.search.trim() ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.balanceFilter !== 'all' ||
    filters.projectQuery.trim();

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          {resultCount === totalCount
            ? `${totalCount} سند`
            : `${resultCount} از ${totalCount} سند`}
        </p>
        {hasActive ? (
          <Button type="button" variant="ghost" size="sm" className="h-8 gap-1 text-xs" onClick={onClear}>
            <X className="h-3.5 w-3.5" />
            پاک کردن فیلترها
          </Button>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[14rem] flex-1">
          <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={filters.search}
            onChange={(e) => onChange({ search: e.target.value })}
            placeholder="جستجو: شماره سند، شرح، تاریخ…"
            className="h-10 pr-9"
          />
        </div>
        {showProjectFilter ? (
          <div className="min-w-[12rem]">
            <Input
              value={filters.projectQuery}
              onChange={(e) => onChange({ projectQuery: e.target.value })}
              placeholder="فیلتر نام پروژه…"
              className="h-10"
            />
          </div>
        ) : null}
        <div className="min-w-[10rem]">
          <PersianDatePickerFa
            value={filters.dateFrom}
            onChange={(v) => onChange({ dateFrom: v })}
            placeholder="از تاریخ…"
          />
        </div>
        <div className="min-w-[10rem]">
          <PersianDatePickerFa
            value={filters.dateTo}
            onChange={(v) => onChange({ dateTo: v })}
            placeholder="تا تاریخ…"
          />
        </div>
        <div className="min-w-[11rem]">
          <SearchableSelect
            value={filters.balanceFilter}
            onChange={(v) =>
              onChange({ balanceFilter: (v as AccountingDocumentsFilterState['balanceFilter']) || 'all' })
            }
            options={BALANCE_OPTIONS}
            placeholder="وضعیت تراز"
          />
        </div>
      </div>
    </div>
  );
};

export default AccountingDocumentsToolbar;
